import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (detail == null) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0B0E14] border border-white/10 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00FFA3] focus:border-transparent px-4 py-3";
  const labelCls =
    "font-mono-tech text-xs uppercase tracking-[0.18em] text-slate-400 mb-2 block";

  return (
    <div
      className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-6 relative overflow-hidden"
      data-testid="login-page"
    >
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="relative z-10 w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-8 mx-auto"
          data-testid="login-logo"
        >
          <Shield size={22} className="text-[#00FFA3]" strokeWidth={1.5} />
          <span className="font-heading font-extrabold text-2xl tracking-tight text-white">
            CYBER<span className="text-[#00FFA3]">VETS</span>
          </span>
        </button>

        <div className="bg-[#151921] border border-white/10 p-8 md:p-10">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-[#00FFA3]" />
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#00FFA3]">
              Admin Access
            </span>
          </div>
          <h1 className="font-heading font-bold text-2xl text-white mb-6">
            Sign in to your dashboard
          </h1>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className={labelCls}>Email</label>
              <input
                data-testid="login-email"
                type="email"
                className={inputCls}
                placeholder="admin@cybervets.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Password</label>
              <input
                data-testid="login-password"
                type="password"
                className={inputCls}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00FFA3] text-[#0B0E14] font-bold px-8 py-4 hover:bg-[#00E5FF] transition-colors disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Signing in...</> : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
